import React, { useEffect, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { debounce } from "lodash";
import {
  Box,
  Button,
  IconButton,
  Tooltip,
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
  Grid,
  Typography,
  Avatar,
  Paper,
  Autocomplete,
  TextField,
  CircularProgress,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import DiamondIcon from "@mui/icons-material/Diamond";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import DataTable from "react-data-table-component";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { contestServices } from "../../services/AllServices";
import errorHandler from "../../utils/errorHandler";
import { ADMIN_ROLES } from "../../utils/constants";
import { useNavigate } from "react-router-dom";
import { Loader } from "../../components/CustomLoader";
import { capitalizeFirstLetter, TrimText } from "../../utils/helpers";
import { fDate, fDate1 } from "../../utils/formatTime";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import SDCoinIcon from "../../components/shared/SDCoinIcon";

export default function ContestManagement() {
  const { user: currentUser } = useSelector((state) => state.auth);
  const navigate = useNavigate();
  const [searchItem, setSearchItem] = useState("");
  const [data, setData] = useState([]);
  const [isOrderChanged, setIsOrderChanged] = useState(false);
  const [isSavingOrder, setIsSavingOrder] = useState(false);
  const [isMoving, setIsMoving] = useState(null);
  const [status, setStatus] = useState([]);
  const [refresh, setRefresh] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loaderDelay, setLoaderDelay] = useState(false);
  const [totalActiveContests, setTotalActiveContests] = useState(0);
  const [totalInactiveContests, setTotalInactiveContests] = useState(0);

  const [openDropdown, setOpenDropdown] = useState(false);
  const [dropdownOptions, setDropdownOptions] = useState([]);
  const [loadingDropdown, setLoadingDropdown] = useState(false);

  const [selectedContest, setSelectedContest] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [isAddingHotContest, setIsAddingHotContest] = useState(false);

  const handleOpenModal = (contest) => {
    setSelectedContest(contest);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setSelectedContest(null);
    setOpenModal(false);
  };

  const handleAddToHotContest = async () => {
    if (!selectedContest) return;
    try {
      setIsAddingHotContest(true);
      const res = await contestServices.addToHotContest({
        id: selectedContest._id,
      });
      if (res.status === 200) {
        toast.success(
          res?.data?.message || "Added to hot contests successfully",
          { toastId: "success-hot" },
        );
        handleCloseModal();
        setRefresh(!refresh);
      } else {
        toast.error(res?.data?.message || "Failed to add to hot contests", {
          toastId: "error-hot",
        });
      }
    } catch (err) {
      errorHandler(err);
    } finally {
      setIsAddingHotContest(false);
    }
  };

  const fetchDropdownOptions = async (query) => {
    try {
      setLoadingDropdown(true);
      const res = await contestServices.getContestForHotContest({
        searchItem: query || "",
        pageNumber: 1,
        pageSize: 50,
      });
      if (res.status === 200) {
        setDropdownOptions(res?.data?.data?.result || []);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingDropdown(false);
    }
  };

  const debouncedDropdownSearch = useCallback(
    debounce((query) => fetchDropdownOptions(query), 400),
    [],
  );

  useEffect(() => {
    if (openDropdown) {
      fetchDropdownOptions(searchItem);
    }
  }, [openDropdown, searchItem]);

  const fetchBidPackage = async (query) => {
    const body = {
      searchItem: query || "",
      status: status,
    };

    try {
      setIsLoading(true);
      setLoaderDelay(false);
      const res = await contestServices.getAllHotContests(body);
      if (res.status === 200) {
        setTimeout(() => {
          setData(res?.data?.data?.contests);
          if (res?.data?.data?.totalActiveContests !== undefined) {
            setTotalActiveContests(res.data.data.totalActiveContests);
          }
          if (res?.data?.data?.totalInactiveContests !== undefined) {
            setTotalInactiveContests(res.data.data.totalInactiveContests);
          }

          setLoaderDelay(true);
        }, 500);
      }
    } catch (err) {
      setData(err?.response?.data?.message?.data);
      errorHandler(err);
      setLoaderDelay(true);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchTotalStats = async () => {
    try {
      const res = await contestServices.getContest({
        searchItem: "",
        pageNumber: 1,
        pageSize: 1000,
        status: [],
      });

      if (res.status === 200 && res?.data?.data?.contests) {
        const allContests = res.data.data.contests;
        setTotalActiveContests(
          allContests.filter((item) => item.status === 1).length,
        );
        setTotalInactiveContests(
          allContests.filter((item) => item.status === 0).length,
        );
      }
    } catch (err) {}
  };

  const handleOrderChange = (id, direction) => {
    const currentIndex = data.findIndex((item) => item._id === id);
    if (currentIndex < 0) return;

    let newIndex = -1;
    if (direction === "up" && currentIndex > 0) {
      newIndex = currentIndex - 1;
    } else if (direction === "down" && currentIndex < data.length - 1) {
      newIndex = currentIndex + 1;
    }

    if (newIndex !== -1) {
      setIsMoving(id);
      const newData = [...data];
      const temp = newData[currentIndex];
      newData[currentIndex] = newData[newIndex];
      newData[newIndex] = temp;

      setTimeout(() => {
        setData(newData);
        setIsOrderChanged(true);
        setTimeout(() => setIsMoving(null), 300);
      }, 50);
    }
  };

  const saveOrder = async () => {
    try {
      setIsSavingOrder(true);
      const orderedIds = data.map((item) => item._id);
      const res = await contestServices.saveHotContestOrder({ orderedIds });
      if (res.status === 200) {
        toast.success("Order saved successfully", { toastId: "success-order" });
        setIsOrderChanged(false);
        setRefresh(!refresh);
      } else {
        toast.error(res?.data?.message || "Failed to save order", {
          toastId: "error-order",
        });
      }
    } catch (err) {
      errorHandler(err);
    } finally {
      setIsSavingOrder(false);
    }
  };

  const deleteData = async (id) => {
    Swal.fire({
      text: "Are you sure you want to remove this contest from Hot Contests?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#00a1da",
      confirmButtonText: "Yes",
      cancelButtonText: "No",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const res = await contestServices.removeFromHotContest({ id });
          if (res?.status === 200) {
            toast.success(res?.data?.message, { toastId: "success" });
            setData((prevData) => prevData.filter((item) => item._id !== id));
            setRefresh(!refresh);
          } else {
            toast.error(res?.data?.message, { toastId: "error" });
          }
        } catch (error) {
          errorHandler(error);
        }
      }
    });
  };

  useEffect(() => {
    fetchBidPackage("");
    // Fetch total statistics on component mount
    fetchTotalStats();
  }, [status, refresh]);

  // Separate effect for view mode changes to avoid unnecessary data fetching

  const columns = [
    {
      name: "S.No.",
      cell: (row, index) => index + 1,
      width: "75px",
    },
    {
      name: "Order",
      center: true,
      cell: (row) => (
        <div style={{ display: "flex", flexDirection: "row" }}>
          <Tooltip title="" placement="">
            <IconButton
              color="primary"
              onClick={() => handleOrderChange(row._id, "up")}
              size="small"
            >
              <ArrowUpwardIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="" placement="">
            <IconButton
              color="primary"
              onClick={() => handleOrderChange(row._id, "down")}
              size="small"
            >
              <ArrowDownwardIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </div>
      ),
      width: "100px",
    },
    {
      name: "Contest Name",
      selector: (row) => TrimText(row.contestName, 20),
      width: "160px",
    },
    {
      name: "Game Type",
      selector: (row) => {
        const contestType = row.contestType || {};
        const type = capitalizeFirstLetter(contestType.type || "");

        const positionMap = {
          27: "Forward",
          26: "Midfielder",
          25: "Defender",
          24: "Goalkeeper",
        };

        const playerRole = positionMap[contestType.playerPosition] || "";
        const actionType = contestType.actionType
          ? capitalizeFirstLetter(contestType.actionType)
          : "";
        const negativeType = contestType.negativeType
          ? capitalizeFirstLetter(contestType.negativeType)
          : "";

        return (
          <div>
            <div>{type}</div>

            {(contestType.type === "action" ||
              contestType.type === "position") &&
              playerRole && (
                <div>
                  <strong>Player Position:</strong> {playerRole}
                </div>
              )}

            {contestType.type === "action" && actionType && (
              <div>
                <strong>Action:</strong> {actionType}
              </div>
            )}

            {contestType.type === "negative-point" && negativeType && (
              <div>
                <strong>Negative Type:</strong> {negativeType}
              </div>
            )}
          </div>
        );
      },
      width: "240px",
    },
    {
      name: "Contest Type",
      selector: (row) => capitalizeFirstLetter(row?.gameType),
      width: "160px",
    },
    {
      name: "Game Duration",
      selector: (row) => capitalizeFirstLetter(row.gameDuration?.type),
      width: "160px",
    },
    {
      name: "Game Duration",
      selector: (row) => {
        const start = fDate1(row.gameDuration?.startTime);
        const end = fDate1(row.gameDuration?.endTime);

        return (
          <div>
            <div>
              <strong>Start:</strong> {start}
            </div>
            <div>
              <strong>End:</strong> {end}
            </div>
          </div>
        );
      },
      width: "200px",
    },
    {
      name: "Created By",
      selector: (row) =>
        capitalizeFirstLetter(row.createdByRole === "users" ? "User" : "Admin"),
      width: "160px",
    },
    {
      name: "Created At",
      selector: (row) => fDate(row.createdAt),
      width: "160px",
    },
    {
      name: "Action",
      center: true,
      cell: (row) => (
        <>
          <Tooltip title="View" placement="top">
            <IconButton
              color="primary"
              onClick={() => {
                navigate(`/contest-management/view/${row?._id}`);
              }}
            >
              <VisibilityIcon />
            </IconButton>
          </Tooltip>
          {currentUser?.role !== ADMIN_ROLES.JUNIOR && (
            <Tooltip title="Remove From Hot Contest" placement="top">
              <IconButton color="primary" onClick={() => deleteData(row._id)}>
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          )}
        </>
      ),
    },
  ];

  return (
    <Box className="contest-management-container">
      {/* Header Section */}
      <Paper elevation={0} className="contest-header-paper">
        <Box className="contest-header-content">
          <Box className="contest-header-left">
            <Avatar className="contest-header-avatar">
              <EmojiEventsIcon className="contest-header-icon" />
            </Avatar>
            <Box>
              <Typography variant="h4" className="contest-header-title">
                Hot Contest Management
              </Typography>
              <Typography variant="body1" className="contest-header-subtitle">
                Manage and monitor all hot contests in your system
              </Typography>
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* Controls Section */}
      <Paper elevation={0} className="contest-controls-paper">
        <Box className="contest-controls-content">
          <Box className="contest-controls-left">
            <Autocomplete
              sx={{ width: 300 }}
              open={openDropdown}
              onOpen={() => setOpenDropdown(true)}
              onClose={() => setOpenDropdown(false)}
              isOptionEqualToValue={(option, value) => option._id === value._id}
              getOptionLabel={(option) => option.contestName || ""}
              options={dropdownOptions}
              loading={loadingDropdown}
              inputValue={searchItem}
              onInputChange={(event, newInputValue) => {
                setSearchItem(newInputValue);
                debouncedDropdownSearch(newInputValue);
              }}
              onChange={(event, newValue) => {
                if (newValue) {
                  handleOpenModal(newValue);
                  setSearchItem("");
                  debouncedDropdownSearch("");
                } else {
                  setSearchItem("");
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Search and Add contest"
                  variant="standard"
                  className="contest-search-input"
                  InputProps={{
                    ...params.InputProps,
                    disableUnderline: true,
                    endAdornment: (
                      <React.Fragment>
                        {loadingDropdown ? (
                          <CircularProgress color="inherit" size={20} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </React.Fragment>
                    ),
                  }}
                />
              )}
            />
          </Box>
          <Box className="contest-controls-right">
            {isOrderChanged && (
              <Button
                variant="contained"
                color="primary"
                onClick={saveOrder}
                disabled={isSavingOrder}
                startIcon={
                  isSavingOrder ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : null
                }
              >
                Save Changes
              </Button>
            )}
          </Box>
        </Box>
      </Paper>

      {/* Content Section */}
      <Paper elevation={0} className="contest-content-paper">
        <Box>
          <DataTable
            columns={columns}
            data={data}
            progressPending={isLoading || !loaderDelay}
            progressComponent={<Loader />}
            className="contest-datatable"
            conditionalRowStyles={[
              {
                when: (row) => row._id === isMoving,
                style: {
                  backgroundColor: "rgba(0, 161, 218, 0.1)",
                  transition: "all 0.3s ease-in-out",
                  animation: "rowSwap 0.3s ease-in-out",
                },
              },
            ]}
          />
          <style>
            {`
                @keyframes rowSwap {
                  0% { transform: translateY(0); opacity: 1; }
                  50% { transform: translateY(-5px); opacity: 0.8; }
                  100% { transform: translateY(0); opacity: 1; }
                }
                .rdt_TableBody [role="row"] {
                  transition: transform 0.3s ease-in-out, background-color 0.3s ease-in-out;
                }
              `}
          </style>
        </Box>
      </Paper>

      <Dialog
        open={openModal}
        onClose={handleCloseModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Contest Details
          <IconButton
            aria-label="close"
            onClick={handleCloseModal}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedContest && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="subtitle1">
                  <strong>Name:</strong> {selectedContest.contestName}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body1">
                  <strong>Game Type:</strong>{" "}
                  {selectedContest.gameType === "playForGems"
                    ? "Play for Gems"
                    : selectedContest.gameType === "playForSDCoins"
                    ? "Play For SD Coins"
                    : capitalizeFirstLetter(selectedContest.gameType)}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body1">
                  <strong>Type:</strong>{" "}
                  {capitalizeFirstLetter(
                    selectedContest.type === "head-to-head"
                      ? "PvP"
                      : selectedContest.type,
                  )}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                {(selectedContest.gameType === "playForGems" || selectedContest.gameType === "playForSDCoins") && (
                  <Typography
                    variant="body1"
                    sx={{ display: "flex", alignItems: "center" }}
                  >
                    <strong>Entry Fee:</strong> &nbsp;{selectedContest.entryFee}
                    &nbsp;
                    {selectedContest.gameType === "playForGems" ? (
                      <DiamondIcon sx={{ fontSize: 18, color: "#1976d2" }} />
                    ) : (
                      <SDCoinIcon size={18} />
                    )}
                  </Typography>
                )}
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body1">
                  <strong>Winners:</strong> {selectedContest.numberOfWinners}
                </Typography>
              </Grid>
              {selectedContest.prizePool !== undefined && (
                <Grid item xs={6}>
                  <Typography variant="body1">
                    <strong>Prize Pool:</strong> {selectedContest.prizePool}
                  </Typography>
                </Grid>
              )}
              {selectedContest.roomSize && (
                <Grid item xs={6}>
                  <Typography variant="body1">
                    <strong>Room Size:</strong>{" "}
                    {capitalizeFirstLetter(selectedContest.roomSize?.type)}
                  </Typography>
                </Grid>
              )}
              <Grid item xs={12}>
                <Typography variant="body1">
                  <strong>Duration:</strong>{" "}
                  {selectedContest.gameDuration?.type}(
                  {fDate1(selectedContest.gameDuration?.startTime)} -{" "}
                  {fDate1(selectedContest.gameDuration?.endTime)})
                </Typography>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleAddToHotContest}
            color="primary"
            variant="contained"
            disabled={isAddingHotContest}
            startIcon={
              isAddingHotContest ? (
                <CircularProgress size={20} color="inherit" />
              ) : null
            }
          >
            Add to Hot Contest
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
